import React, { useState, useEffect } from "react";
import axios from "axios";
import { FileText, Wand2, Info } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { PdfFieldGuideBody } from "@/components/enrollment/PdfFieldGuideModal";
import FormBuilderPage from "./FormBuilderPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PdfBuilderContent = () => {
  const [tab, setTab] = useState("start");

  return (
    <div data-testid="pdf-form-builder-content">
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-600 p-6 mb-6 text-white">
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          <FileText className="w-6 h-6" /> PDF Field Name Guide
        </h1>
        <p className="text-sm text-white/90">
          For centers building enrollment PDFs in Adobe Acrobat Pro · Guide v1.0
        </p>
        <p className="text-xs text-white/80 mt-2 italic">
          We auto-fill parent, child, physician and insurance info from your PDFs — but only if your Adobe field names match our list.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 flex gap-2 items-start text-sm text-blue-900">
        <Info className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
        <div>
          <div className="font-semibold">Where do I upload the finished PDFs?</div>
          <div className="text-xs mt-1">
            Head to <strong>Children → Compliance Records → Enrollment Packet</strong>. That page has the Blank Form Templates and per-child upload rows. No time to build from scratch? Each upload row there offers a starter PDF with all field names pre-configured.
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <PdfFieldGuideBody tab={tab} onTabChange={setTab} contentClassName="px-6 py-5" />
      </div>
    </div>
  );
};

export default function FormBuilderHubPage() {
  const { user } = useAuth();
  const [permOverrides, setPermOverrides] = useState({});

  useEffect(() => {
    axios.get(`${API}/owner/permissions`)
      .then(r => setPermOverrides(r.data?.permissions || {}))
      .catch(() => {});
  }, []);

  // Same rule as AppShell: owner always allowed; no stored override = allow
  const hasPerm = (key) => {
    if (user?.role === "owner") return true;
    const rolePerms = permOverrides[key] || {};
    if (Object.keys(rolePerms).length === 0) return true;
    return rolePerms[user?.role] !== false;
  };
  const canPdf = hasPerm("pdf_form_builder");
  const canDigital = hasPerm("digital_form_builder");

  const initial = new URLSearchParams(window.location.search).get("mode") === "digital" ? "digital" : "pdf";
  const [mode, setMode] = useState(initial);

  // If the current sub-tab is toggled off for this role, land on the allowed one
  useEffect(() => {
    if (mode === "pdf" && !canPdf && canDigital) setMode("digital");
    if (mode === "digital" && !canDigital && canPdf) setMode("pdf");
  }, [canPdf, canDigital, mode]);

  // Keep URL synced when user switches sub-tabs
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("mode", mode);
    window.history.replaceState({}, "", url);
  }, [mode]);

  return (
    <AppShell pageTitle="Form Builder" breadcrumb>
      <div className="flex flex-wrap gap-1 mb-6 bg-slate-100 rounded-2xl p-1 w-fit" data-testid="form-builder-hub-tabs">
        {canPdf && (
          <button
            type="button"
            onClick={() => setMode("pdf")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              mode === "pdf" ? "bg-white shadow text-teal-700" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="form-builder-tab-pdf"
          >
            <FileText className="w-4 h-4" />
            PDF Form Builder
          </button>
        )}
        {canDigital && (
          <button
            type="button"
            onClick={() => setMode("digital")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              mode === "digital" ? "bg-white shadow text-blue-700" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="form-builder-tab-digital"
          >
            <Wand2 className="w-4 h-4" />
            Digital Form Builder
          </button>
        )}
      </div>

      {!canPdf && !canDigital ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-sm text-amber-900" data-testid="form-builder-no-access">
          Your role doesn&apos;t have access to either form builder. Ask an owner to enable it in Owner Tools → Permissions.
        </div>
      ) : mode === "pdf" && canPdf ? (
        <PdfBuilderContent />
      ) : (
        <FormBuilderPage embedded />
      )}
    </AppShell>
  );
}
